/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { DraggableTab } from '@components/molecules'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import Tabs from '@mui/material/Tabs'

import { useTheme, Theme } from '@mui/material/styles'
import {
	useAppSelector,
	useAppDispatch,
	configActions,
	TabType,
	ConfigStateType,
} from '@redux/index'
import { useNavigate } from 'react-router-dom'
import { yellow } from '@mui/material/colors'

export const MainNavTabs = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()
	const theme = useTheme<Theme>()

	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const selectedTabIdx: string = useAppSelector(state => state.config.selectedTabIdx)
	const tabList: TabType[] = useAppSelector(state => state.config.tabList)

	// variables
	const variant = tabList.length === 1 ? 'standard' : 'scrollable'

	const onTabClick = React.useCallback(
		(event: React.SyntheticEvent, newValue: string) => {
			const newSelectedTabIdx: string = parseInt(newValue.replace('Tab ', '')).toString()
			const newSelectedTab: TabType = tabList[parseInt(newSelectedTabIdx) - 1]

			navigate(newSelectedTab.path)
		},
		[tabList],
	)

	const primaryColor: string = theme.palette.primary['main']

	const onDragEnd = React.useCallback(
		(result: any) => {
			const selectedTabId: string = tabList[parseInt(selectedTabIdx) - 1].id
			const destinationIdx: number = result.destination.index
			const newTabs: TabType[] = Array.from([...tabList])
			const draggedTab: TabType = newTabs.splice(result.source.index, 1)[0]
			newTabs.splice(destinationIdx, 0, draggedTab)

			const newSelectedTabIdx: number = newTabs.findIndex(
				(aTab: TabType) => aTab.id === selectedTabId,
			)

			reduxDispatch(configActions.TAB_LIST([...newTabs]))
			reduxDispatch(configActions.SELECTED_TAB_IDX((newSelectedTabIdx + 1).toString()))
		},
		[tabList],
	)

	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<View css={style}>
				<Droppable droppableId="1" direction="horizontal">
					{droppableProvided => (
						<View
							className="droppableInnerView"
							ref={droppableProvided.innerRef}
							{...droppableProvided.droppableProps}
						>
							<Tabs
								className="tabList"
								value={`Tab ${selectedTabIdx}`}
								onChange={onTabClick}
								indicatorColor="secondary"
								textColor="inherit"
								scrollButtons
								allowScrollButtonsMobile
								aria-label="Draggable Tabs"
								variant={variant}
								TabIndicatorProps={{
									style:
										selectedTabIdx === '1'
											? {
													background: 'rgba(0,0,0,0)',
											  }
											: {
													background: mode === 'admin' ? yellow[500] : primaryColor,
											  },
								}}
							>
								{tabList.map((aTab: TabType, idx: number) => {
									return (
										<DraggableTab
											key={aTab.id}
											id={aTab.id}
											path={aTab.path}
											i18n={aTab.i18n}
											index={idx}
											label={aTab.name}
											value={`Tab ${idx + 1}`}
										/>
									)
								})}
								{droppableProvided ? droppableProvided.placeholder : null}
							</Tabs>
						</View>
					)}
				</Droppable>
			</View>
		</DragDropContext>
	)
}

const style = css({
	zIndex: 3,
	flexGrow: 1,
	height: '100%',
	minHeight: '100%',
	paddingLeft: '1rem',

	'.droppableInnerView': {
		display: 'flex',
		alignItems: 'flex-start',
		height: '100%',
		minHeight: '100%',
	},

	'.tabList': {
		display: 'flex',
		alignItems: 'center',
		maxWidth: 'calc(100vw - 17rem)',
	},
})
